import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw, TrendingUp, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const yen = (n) => `¥${Number(n || 0).toLocaleString("ja-JP")}`;
const weekday = (s) => (s ? ["日", "月", "火", "水", "木", "金", "土"][new Date(s).getDay()] : "");
const fmtDate = (s) => {
  if (!s) return "—";
  const [, m, d] = s.split("-");
  return `${parseInt(m)}/${parseInt(d)}（${weekday(s)}）`;
};

// 今月の初日・末日
const monthRange = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return [iso(first), iso(last)];
};

export default function SalesSummary() {
  const [defStart, defEnd] = monthRange();
  const [start, setStart] = useState(defStart);
  const [end, setEnd] = useState(defEnd);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (s, e) => {
    if (!s || !e) return;
    if (s > e) {
      toast.error("開始日は終了日より前にしてください");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/sales-summary?start=${s}&end=${e}`, getAuthHeaders());
      setData(res.data);
    } catch (err) {
      toast.error("売上集計の取得に失敗しました");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(defStart, defEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const days = data?.days || [];
  const totals = data?.totals || { subtotal: 0, tax: 0, total: 0, by_rate: {} };
  const rateKeys = Object.keys(totals.by_rate || {}).sort();

  const handleCSV = () => {
    if (!days.length) return;
    const rows = [["納品日", "税抜き合計", "税額", "税込合計", "8%対象(税抜)", "8%税額", "10%対象(税抜)", "10%税額", "明細数", "金額未計上"]];
    days.forEach((d) => {
      rows.push([
        d.delivery_date,
        d.subtotal, d.tax, d.total,
        d.by_rate?.["8"]?.subtotal || 0, d.by_rate?.["8"]?.tax || 0,
        d.by_rate?.["10"]?.subtotal || 0, d.by_rate?.["10"]?.tax || 0,
        d.item_count, d.unpriced_count,
      ]);
    });
    rows.push(["合計", totals.subtotal, totals.tax, totals.total,
      totals.by_rate?.["8"]?.subtotal || 0, totals.by_rate?.["8"]?.tax || 0,
      totals.by_rate?.["10"]?.subtotal || 0, totals.by_rate?.["10"]?.tax || 0, "", ""]);
    const csv = "\ufeff" + rows.map((r) => r.join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `売上集計_${start}_${end}.csv`;
    a.click();
    toast.success("CSVをダウンロードしました");
  };

  return (
    <div className="space-y-5" data-testid="sales-summary">
      <div className="flex items-center gap-2">
        <TrendingUp className="h-5 w-5 text-emerald-600" />
        <h2 className="text-xl font-semibold tracking-tight text-slate-800">売上集計</h2>
      </div>
      <p className="text-sm text-slate-600">
        納品日ごとの税抜き合計・税額・税込合計を表示します。全発注（未確定含む・取消除外）が対象です。単価は価格期間を優先し、なければ店舗ランク別価格を使用します。税率は商品ごとの設定（未設定は8%）で計算します。
      </p>

      {/* 期間 */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-slate-600">開始（納品日）</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="sales-start" className="mt-1 h-9 w-44 rounded-md" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">終了（納品日）</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="sales-end" className="mt-1 h-9 w-44 rounded-md" />
        </div>
        <Button onClick={() => fetchData(start, end)} disabled={loading} data-testid="sales-fetch" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          表示
        </Button>
        <Button onClick={handleCSV} disabled={!days.length} variant="outline" data-testid="sales-csv" className="h-9 rounded-md border-emerald-600 text-emerald-700 hover:bg-emerald-50 ml-auto">
          <Download className="h-4 w-4 mr-1" />
          CSV
        </Button>
      </div>

      {/* サマリカード */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs text-slate-500">税抜き合計</div>
          <div className="text-2xl font-bold text-slate-800 mt-1 tabular-nums" data-testid="sales-total-subtotal">{yen(totals.subtotal)}</div>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs text-amber-700">税額</div>
          <div className="text-2xl font-bold text-amber-700 mt-1 tabular-nums" data-testid="sales-total-tax">{yen(totals.tax)}</div>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-xs text-emerald-700">税込合計</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1 tabular-nums" data-testid="sales-total-total">{yen(totals.total)}</div>
        </div>
      </div>

      {/* 日ごと表 */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : days.length === 0 ? (
        <div className="text-center py-12 border border-slate-200 rounded-2xl bg-white text-slate-500" data-testid="sales-empty">
          この期間の発注はありません
        </div>
      ) : (
        <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">納品日</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500">税抜き合計</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500">税額</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500">税込合計</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500">内訳（税率別 税抜/税額）</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500">明細</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.delivery_date} className="border-b border-slate-100" data-testid={`sales-row-${d.delivery_date}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-700">{fmtDate(d.delivery_date)}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-800">{yen(d.subtotal)}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-amber-700">{yen(d.tax)}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums font-semibold text-emerald-700">{yen(d.total)}</td>
                  <td className="px-4 py-2.5 text-right text-xs text-slate-500">
                    {Object.keys(d.by_rate || {}).sort().map((r) => (
                      <div key={r}>{r}%: {yen(d.by_rate[r].subtotal)} / {yen(d.by_rate[r].tax)}</div>
                    ))}
                    {Object.keys(d.by_rate || {}).length === 0 && "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs text-slate-500">
                    {d.item_count}件
                    {d.unpriced_count > 0 && (
                      <span className="ml-1 text-rose-500" title="価格未計上（リストなし発注や価格未登録商品）">
                        (未計上{d.unpriced_count})
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-800 text-white font-semibold" data-testid="sales-total-row">
                <td className="px-4 py-3">合計</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums">{yen(totals.subtotal)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums">{yen(totals.tax)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums">{yen(totals.total)}</td>
                <td className="px-4 py-3 text-right text-xs">
                  {rateKeys.map((r) => (
                    <div key={r}>{r}%: {yen(totals.by_rate[r].subtotal)} / {yen(totals.by_rate[r].tax)}</div>
                  ))}
                </td>
                <td className="px-4 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-400">
        ※ 税額は納品日ごと・税率ごとに合算してから四捨五入して算出しています（参考値）。正式な請求額はやさまるデスク側の計算をご確認ください。
      </p>
    </div>
  );
}
